<template>
    <div class="chat-count-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>{{ $t('report.sessionsOverall') }}</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <EChartsLine
            v-if="!tableMode"
            :labels="getXAxisData()"
            :datasets="getChartDatasets()"
            :height="320"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard
                :title="$t('report.total')"
                :value="formatStat(stats.TotalSessionCount).value"
                :unit="formatStat(stats.TotalSessionCount).unit"
                :description="$t('report.totalSessionCountDesc')"
            />
            <StatCard
                :title="getTitle('average')"
                :value="formatStat(stats.AverageSessionCount).value"
                :unit="formatStat(stats.AverageSessionCount).unit"
                :description="$t('report.averageSessionCountDesc')"
            />
            <StatCard
                :title="getTitle('max')"
                :value="formatStat(stats.MaxSessionCount).value"
                :unit="formatStat(stats.MaxSessionCount).unit"
                :description="$t('report.maxSessionCountDesc')"
            />
            <StatCard
                :title="getTitle('min')"
                :value="stats.MinSessionCount"
                :unit="$t('report.session')"
                :description="$t('report.minSessionCountDesc')"
            />
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import EChartsLine from "../../../components/charts/EChartsLine.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";

const log = log2("OverallChatCountChart");
export default Vue.extend({
    name: "OverallChatCountChart",
    components: { ChartDoddleIcon, TableView, IconButton, StatCard, EChartsLine },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            stats: {
                TotalSessionCount: 0,
                AverageSessionCount: 0,
                MaxSessionCount: 0,
                MinSessionCount: 0,
            },
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
            },
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        formatStat(value) {
            return {
                value: value !== 0 ? value : this.$t("report.noEnoughtData"),
                unit: value !== 0 ? this.$t("report.session") : "",
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = this.$el;
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.sessionOverall(this.filters)
                .then((res) => {
                    log("sessionOverall data fetched", res);
                    this.graphData = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: this.$t("report.sessionCount"),
                    dataIndex: "sessionCount",
                    key: "sessionCount",
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            return buckets.map((bucket) => {
                const sessionCount = bucket.sessionCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return { date, sessionCount };
            });
        },
        getStats() {
            const buckets = this.graphData?.buckets || [];
            const sessionCounts = buckets.map((x) => x.sessionCount);
            const isEmpty = sessionCounts.length === 0;

            const TotalSessionCount = isEmpty ? 0 : _.chain(sessionCounts).sum().value();
            const AverageSessionCount = isEmpty ? 0 : parseInt(_.mean(sessionCounts).toFixed(1));
            const MaxSessionCount = isEmpty ? 0 : parseInt(_.max(sessionCounts).toFixed(1));
            const MinSessionCount = isEmpty ? 0 : parseInt(_.min(sessionCounts).toFixed(1));

            const startDate = moment(this.filters.startDate);
            const endDate = moment(this.filters.endDate);

            let HourlyAverageSessionCount = 0;

            if (startDate.isSame(endDate, "day")) {
                const totalHours = endDate.diff(startDate, "hours") + 1;
                HourlyAverageSessionCount = isEmpty ? 0 : parseInt((TotalSessionCount / totalHours).toFixed(1));
            }

            return {
                TotalSessionCount,
                AverageSessionCount: startDate.isSame(endDate, "day") ? HourlyAverageSessionCount : AverageSessionCount,
                MaxSessionCount,
                MinSessionCount,
                isHourlyAverage: startDate.isSame(endDate, "day"),
            };
        },
        getTitle(type) {
            const hourlyText = this.$t("report.hourly");
            if (this.stats.isHourlyAverage) {
                return `${this.$t(`report.${type}`)} ${hourlyText}`;
            }
            return this.$t(`report.${type}`);
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    return moment(item.date).format("MMM");
                }
            });
        },
        getChartDatasets() {
            const { buckets } = this.graphData;
            const data = buckets.map((bucket) => bucket.sessionCount);
            return [
                {
                    label: this.$t("report.sessionCount"),
                    data,
                    fill: true,
                    backgroundColor: "rgba(59, 130, 246, 0.12)",
                    borderColor: "rgba(59, 130, 246, 1)",
                    borderWidth: 2,
                    pointRadius: 3,
                    pointBackgroundColor: "rgba(59, 130, 246, 1)",
                    tension: 0.4,
                },
            ];
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;
            const data = buckets.map((bucket) => {
                const sessionCount = bucket.sessionCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return { date, sessionCount };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                { date: this.$t("report.total"), sessionCount: stats.TotalSessionCount },
                { date: this.$t("report.average"), sessionCount: stats.AverageSessionCount },
                { date: this.$t("report.max"), sessionCount: stats.MaxSessionCount },
                { date: this.$t("report.min"), sessionCount: stats.MinSessionCount },
            ];
            expertToExcel(excelData, `chat_session_count_timeseries.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: this.$el,
            name: "wave",
        });
        this.stats = this.getStats();
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.chat-count-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .echarts-line-wrapper {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.chat-count-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);
                border-radius: 6px;
                overflow: hidden;

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>